// Talks directly to a USB receipt printer from the browser via the WebUSB
// API, bypassing the OS print system entirely — this is what makes raw
// ESC/POS bytes (escposReceipt.ts) actually reach the printer, since
// window.print() always rasterizes to a PDF regardless of the destination.
//
// No @types package for WebUSB is installed in this project, so device
// objects are typed `any` here rather than pulling in a new type-defs
// dependency for this one feature — the actual API surface used is small
// and documented inline.
//
// Chrome/Edge only (not Firefox/Safari), and requires HTTPS or localhost.
// A device must first be granted via pairPrinter() (a real user gesture,
// e.g. clicking "Generate Bill" — WebUSB refuses requestDevice() outside
// one); the browser remembers the grant per-origin after that, so
// getPairedPrinter() can reconnect silently on later prints.

export const isWebUSBSupported = (): boolean =>
  typeof navigator !== "undefined" && !!(navigator as any).usb;

// Identifies which of possibly several origin-granted devices is actually
// "the" receipt printer. navigator.usb.getDevices() returns every device
// this origin has ever been granted (WebUSB grants don't get revoked just
// because a printer was unplugged/replaced) — without this, a second grant
// (e.g. a replacement printer paired without revoking the old one, or an
// unrelated USB device accidentally picked once) would make devices[0]
// resolve to the wrong hardware, silently failing every print until
// someone notices and manually clears site permissions.
const PAIRED_PRINTER_KEY = "pos-usb-printer-id";

// vendorId:productId identify a device *model*, not a specific physical
// unit — many cheap thermal printers don't expose a USB serial number, so
// two granted devices of the identical model both collapse to the same
// key. getPairedPrinter() below treats that as ambiguous rather than
// picking whichever happens to enumerate first (not guaranteed stable
// across reboots/replugging/hub renumbering).
const deviceId = (device: any): string => `${device.vendorId}:${device.productId}:${device.serialNumber ?? ""}`;

const rememberPairedPrinter = (device: any): void => {
  try {
    localStorage.setItem(PAIRED_PRINTER_KEY, deviceId(device));
  } catch {
    // localStorage unavailable (private browsing, quota, etc.) — printing
    // still works this session, just without a remembered preference for
    // next time, so getPairedPrinter() falls back to its single-device case.
  }
};

// requestDevice() has no vendor/product filter (see pairPrinter() below),
// so a first-time pairing attempt could accidentally grant an unrelated
// USB peripheral — a cash drawer, a webcam control interface — that
// happens to expose a bulk OUT endpoint. Checked via device.configurations
// (populated from descriptors at discovery time, unlike device.configuration
// which is only set after open()+selectConfiguration()), so this doesn't
// require actually opening the device just to sanity-check it.
const hasUsbOutEndpoint = (device: any): boolean => {
  const configurations = device.configurations ?? [];
  return configurations.some((config: any) =>
    (config.interfaces ?? []).some((iface: any) =>
      (iface.alternates ?? []).some((alt: any) =>
        (alt.endpoints ?? []).some((ep: any) => ep.direction === "out")
      )
    )
  );
};

export const getPairedPrinter = async (): Promise<any | null> => {
  if (!isWebUSBSupported()) return null;
  const devices: any[] = (await (navigator as any).usb.getDevices()).filter(hasUsbOutEndpoint);
  if (devices.length === 0) return null;
  if (devices.length === 1) return devices[0]; // unambiguous regardless of any stored preference

  let rememberedId: string | null = null;
  try {
    rememberedId = localStorage.getItem(PAIRED_PRINTER_KEY);
  } catch {
    // ignore — treated the same as "no preference stored" below
  }
  const matches = rememberedId ? devices.filter((d) => deviceId(d) === rememberedId) : [];
  // Anything other than exactly one match: don't guess. Zero matches, or
  // more than one device sharing the same id (e.g. two identical-model
  // printers with no distinguishing serial number) both send the caller
  // back through pairPrinter() to disambiguate explicitly via the picker,
  // rather than silently trusting whichever happens to enumerate first.
  return matches.length === 1 ? matches[0] : null;
};

// Must be called synchronously from within a user-gesture handler (a click
// handler is fine; anything after an `await` in that handler is not).
export const pairPrinter = async (): Promise<any | null> => {
  if (!isWebUSBSupported()) return null;
  try {
    // No vendor/product filter: thermal receipt printers rarely advertise
    // the standard USB Printer interface class, so filtering risks hiding
    // the user's actual printer from the picker. Let them pick manually.
    const device = await (navigator as any).usb.requestDevice({ filters: [] });
    if (!device) return null;
    if (!hasUsbOutEndpoint(device)) {
      // Nothing stops the picker from granting an unrelated peripheral
      // (a cash drawer, a webcam control interface) that happens to expose
      // a bulk OUT endpoint... but if it has *none* at all, it's
      // definitely not something sendRawToUsbPrinter() could ever use.
      // Note this can't revoke the WebUSB grant itself (no API for that) —
      // just makes sure this code never treats it as "the" printer.
      return null;
    }
    rememberPairedPrinter(device);
    return device;
  } catch {
    return null; // user cancelled the device picker
  }
};

// None of navigator.usb's device methods have a built-in timeout — a
// wedged/unresponsive printer (a real risk with cheap thermal-printer
// firmware, and easy to end up in after enough retried/aborted transfers)
// leaves the awaited promise simply never settling. Without this, that
// hangs the calling UI (the print button) forever instead of falling back
// to the browser print dialog the way every other failure mode here does.
const TRANSFER_TIMEOUT_MS = 5000;
const withTimeout = <T,>(promise: Promise<T>, ms: number, label: string): Promise<T> =>
  new Promise((resolve, reject) => {
    const timer = setTimeout(
      () => reject(new Error(`${label} timed out after ${ms}ms — the printer may be unresponsive`)),
      ms
    );
    promise.then(
      (value) => { clearTimeout(timer); resolve(value); },
      (err) => { clearTimeout(timer); reject(err); }
    );
  });

export const sendRawToUsbPrinter = async (device: any, data: Uint8Array): Promise<void> => {
  // Every exit path — success or failure — must close the device. A thrown
  // error partway through (e.g. claimInterface() failing because another
  // driver holds the interface) previously skipped close() entirely,
  // leaving Chrome holding the device open indefinitely — which then blocks
  // CUPS *and* any later retry from opening it at all, since a USB device
  // only accepts one open handle at a time. Cleanup calls below are still
  // best-effort (a timeout there is logged, not thrown) — a device that
  // won't respond to release/close isn't something retrying harder will
  // fix, and rethrowing here would mask whatever real error sent us into
  // this finally block in the first place.
  //
  // open() itself is deliberately *inside* this try, not before it: if
  // open() times out, the underlying call has no cancellation and may
  // still succeed asynchronously afterward, with nothing left to close it.
  // `opened` distinguishes that case (best-effort, non-blocking close
  // attempt below) from a genuine open() failure (nothing to close).
  let opened = false;
  try {
    await withTimeout(device.open(), TRANSFER_TIMEOUT_MS, "open()");
    opened = true;
    if (device.configuration === null) {
      await withTimeout(device.selectConfiguration(1), TRANSFER_TIMEOUT_MS, "selectConfiguration()");
    }

    let interfaceNumber = -1;
    let endpointNumber = -1;
    let packetSize = 0;
    for (const iface of device.configuration.interfaces) {
      for (const alt of iface.alternates) {
        const outEndpoint = alt.endpoints.find((e: any) => e.direction === "out");
        if (outEndpoint) {
          interfaceNumber = iface.interfaceNumber;
          endpointNumber = outEndpoint.endpointNumber;
          packetSize = outEndpoint.packetSize ?? 0;
          break;
        }
      }
      if (interfaceNumber !== -1) break;
    }
    if (interfaceNumber === -1) {
      throw new Error("No USB OUT endpoint found on this device — is it actually a printer?");
    }

    await withTimeout(device.claimInterface(interfaceNumber), TRANSFER_TIMEOUT_MS, "claimInterface()");
    try {
      // A single large transferOut() with the whole receipt can overrun a
      // cheap thermal printer's small receive buffer — USB bulk transfers
      // are supposed to be hardware flow-controlled (NAK when the device
      // isn't ready), but plenty of low-cost printer firmware doesn't
      // implement that correctly and just wedges instead, accepting the
      // transfer at the USB layer (no error) while the print engine never
      // actually consumes it. Writing in small chunks with a short pause
      // between them, like most WebUSB receipt-printer implementations do,
      // gives that firmware time to drain its buffer between writes.
      const CHUNK_SIZE = 512;
      const CHUNK_DELAY_MS = 10;
      const writeChunk = async (chunk: Uint8Array): Promise<void> => {
        // Per the WebUSB spec, transferOut() *resolves* (doesn't reject) on
        // a stall or a short write — a flaky cable/hub or a printer with a
        // small buffer can silently produce a blank/garbled receipt while
        // this call reports success, unless the result is actually checked.
        let result = await withTimeout<any>(device.transferOut(endpointNumber, chunk), TRANSFER_TIMEOUT_MS, "transferOut()");
        if (result.status === "stall") {
          // A halted endpoint — commonly left over from a previous transfer
          // that the OS driver or an earlier browser session didn't clean up
          // — is a well-documented, recoverable WebUSB condition: clearing
          // it and retrying once resolves it for most thermal printers.
          // Without this, a receipt that printed fine before this
          // endpoint's first stall would fail on every subsequent attempt,
          // since nothing ever un-halts it.
          await withTimeout(device.clearHalt("out", endpointNumber), TRANSFER_TIMEOUT_MS, "clearHalt()");
          result = await withTimeout<any>(device.transferOut(endpointNumber, chunk), TRANSFER_TIMEOUT_MS, "transferOut() retry");
        }
        if (result.status !== "ok") {
          throw new Error(`USB transfer failed with status "${result.status}"`);
        }
        if (result.bytesWritten !== chunk.byteLength) {
          throw new Error(`USB transfer incomplete: wrote ${result.bytesWritten} of ${chunk.byteLength} bytes`);
        }
      };

      for (let offset = 0; offset < data.byteLength; offset += CHUNK_SIZE) {
        await writeChunk(data.subarray(offset, offset + CHUNK_SIZE));
        if (offset + CHUNK_SIZE < data.byteLength) {
          await new Promise((resolve) => setTimeout(resolve, CHUNK_DELAY_MS));
        }
      }
      // A bulk transfer whose length is an exact multiple of the endpoint's
      // max packet size ends on a full packet, not a short one — the only
      // way USB signals "transfer done" to the device. Many printer
      // firmwares then sit waiting for the rest of a packet that's never
      // coming: the job shows as received/queued but never actually prints.
      // An explicit zero-length packet is the standard fix, and a no-op for
      // any printer that didn't need it. Only the length of the *final*
      // chunk actually sent determines this, not the total — each
      // transferOut() call is its own logical USB transfer.
      const lastChunkLength = data.byteLength === 0 ? 0 : ((data.byteLength - 1) % CHUNK_SIZE) + 1;
      if (data.byteLength > 0 && packetSize > 0 && lastChunkLength % packetSize === 0) {
        // Routed through writeChunk (as a 0-byte chunk) rather than a bare
        // transferOut() so this gets the same status/stall-retry check as
        // every real data chunk — an unchecked result here would silently
        // report success even if this terminating packet itself stalled or
        // never landed, reintroducing exactly the failure mode the rest of
        // this function exists to catch.
        await writeChunk(new Uint8Array(0));
      }
    } finally {
      await withTimeout(device.releaseInterface(interfaceNumber), TRANSFER_TIMEOUT_MS, "releaseInterface()").catch((err) =>
        console.warn("Failed to release the USB printer interface (continuing):", err)
      );
    }
  } finally {
    if (opened) {
      await withTimeout(device.close(), TRANSFER_TIMEOUT_MS, "close()").catch((err) =>
        console.warn("Failed to close the USB printer device (continuing):", err)
      );
    } else {
      // open() itself timed out — not awaited, since a device that won't
      // even respond to open() may well not respond to close() either,
      // and we've already given up waiting once. Fire-and-forget gives a
      // background-succeeding open() a chance to still be released,
      // without risking a second indefinite hang here.
      device.close().catch(() => {});
    }
  }
};
